import { useState, useRef, useEffect } from 'react'
import { verifyTwoFactor, resendTwoFactor } from '../../services/authService'
import { SlimLayout } from './SlimLayout'
import { Logo } from '../LandingPage/Logo'
import { startSession } from '../../services/sessionCookie'

const OTP_LENGTH = 6
const RESEND_COOLDOWN_SECONDS = 60

/**
 * The password login's second factor by default. The email-code sign-in reuses
 * the same UI by supplying its own verify/resend calls, so there is one OTP
 * screen rather than two that drift apart.
 */
export default function TwoFactorVerification({
	pendingLoginToken,
	maskedEmail,
	onCancel,
	onSuccess,
	onVerify = verifyTwoFactor,
	extensionOnly,
	onResend = resendTwoFactor,
	title = 'Verify your identity',
}) {
	const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''))
	const [loading, setLoading] = useState(false)
	const [errorText, setErrorText] = useState('')
	const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS)
	const inputRefs = useRef([])

	useEffect(() => {
		inputRefs.current[0]?.focus()
	}, [])

	useEffect(() => {
		if (resendCooldown <= 0) return
		const timer = setTimeout(() => setResendCooldown(prev => prev - 1), 1000)
		return () => clearTimeout(timer)
	}, [resendCooldown])

	const handleDigitChange = (index, value) => {
		if (!/^\d?$/.test(value)) return

		const updated = [...digits]
		updated[index] = value
		setDigits(updated)
		setErrorText('')

		if (value && index < OTP_LENGTH - 1) {
			inputRefs.current[index + 1]?.focus()
		}

		// Auto-submit when all digits are filled
		if (value && index === OTP_LENGTH - 1 && updated.every(d => d)) {
			handleSubmit(updated.join(''))
		}
	}

	const handleKeyDown = (index, e) => {
		if (e.key === 'Backspace' && !digits[index] && index > 0) {
			inputRefs.current[index - 1]?.focus()
		}
	}

	const handlePaste = e => {
		e.preventDefault()
		const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH)
		if (!pasted) return

		const updated = [...digits]
		for (let i = 0; i < pasted.length; i++) {
			updated[i] = pasted[i]
		}
		setDigits(updated)

		const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1)
		inputRefs.current[focusIndex]?.focus()

		if (updated.every(d => d)) {
			handleSubmit(updated.join(''))
		}
	}

	const handleSubmit = async (code) => {
		const otpCode = code || digits.join('')
		if (otpCode.length !== OTP_LENGTH) {
			setErrorText('Please enter the full 6-digit code')
			return
		}

		setLoading(true)
		setErrorText('')

		try {
			const data = await onVerify({ pendingLoginToken, otpCode, extensionOnly })

			// Handed up whole: a caller may get something other than a token back,
			// such as the email-code flow asking for terms on a new address.
			if (onSuccess) {
				onSuccess(data)
			} else if (data.token) {
				startSession(data.token)
				window.location = '/loadboard'
			}
		} catch (err) {
			const errorMsg = err.response?.data?.error || 'Verification failed'
			setErrorText(errorMsg)
			setDigits(Array(OTP_LENGTH).fill(''))
			inputRefs.current[0]?.focus()
		} finally {
			setLoading(false)
		}
	}

	const handleResend = async () => {
		if (resendCooldown > 0) return

		try {
			await onResend({ pendingLoginToken })
			setResendCooldown(RESEND_COOLDOWN_SECONDS)
			setErrorText('')
			setDigits(Array(OTP_LENGTH).fill(''))
			inputRefs.current[0]?.focus()
		} catch (err) {
			const errorMsg = err.response?.data?.error || 'Failed to resend code'
			setErrorText(errorMsg)
		}
	}

	return (
		<SlimLayout>
			<div className="flex">
				<Logo color={'text-gray-600 text-lg'} height={'45px'} width={'45px'} />
			</div>

			<h2 className="mt-4 mb-2 text-lg font-semibold text-gray-900">
				{title}
			</h2>

			<p className="text-sm text-gray-600 mb-6">
				We sent a verification code to <span className="font-medium">{maskedEmail}</span>
			</p>

			{errorText && (
				<div className="text-red-500 text-sm mb-4">{errorText}</div>
			)}

			<div className="flex gap-2 justify-center mb-6" onPaste={handlePaste}>
				{digits.map((digit, i) => (
					<input
						key={i}
						ref={el => (inputRefs.current[i] = el)}
						type="text"
						inputMode="numeric"
						maxLength={1}
						value={digit}
						onChange={e => handleDigitChange(i, e.target.value)}
						onKeyDown={e => handleKeyDown(i, e)}
						className="w-12 h-14 text-center text-xl font-semibold border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
						disabled={loading}
					/>
				))}
			</div>

			<button
				onClick={() => handleSubmit()}
				disabled={loading || digits.some(d => !d)}
				className="w-full py-2.5 px-4 text-sm font-semibold bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed mb-4"
			>
				{loading ? (
					<span className="loading loading-spinner loading-md mr-2"></span>
				) : null}
				Verify
			</button>

			<div className="flex justify-between items-center text-sm">
				<button
					onClick={handleResend}
					disabled={resendCooldown > 0}
					className={`font-medium ${
						resendCooldown > 0
							? 'text-gray-400 cursor-not-allowed'
							: 'text-blue-600 hover:underline cursor-pointer'
					}`}
				>
					{resendCooldown > 0 ? `Resend code (${resendCooldown}s)` : 'Resend code'}
				</button>

				<button
					onClick={onCancel}
					className="text-gray-500 hover:text-gray-700 cursor-pointer"
				>
					Cancel
				</button>
			</div>
		</SlimLayout>
	)
}
